import { m } from 'framer-motion';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useBoolean } from 'src/hooks/use-boolean';

import { useGetFeedbackTypes } from 'src/api/services/system_definition/systemService';
import { useFeedbackOperations } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendService';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';

// ----------------------------------------------------------------------

export function FeedbackButton({ ...other }) {
  const dialog = useBoolean();
  const [feedbackTypeId, setFeedbackTypeId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { feedbackTypes, feedbackTypesLoading } = useGetFeedbackTypes();
  const { submitFeedback } = useFeedbackOperations();

  const handleClose = useCallback(() => {
    dialog.onFalse();
    setFeedbackTypeId('');
    setMessage('');
  }, [dialog]);

  const handleSubmit = useCallback(async () => {
    if (!feedbackTypeId || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await submitFeedback({
        FeedbackTypeId: parseInt(feedbackTypeId, 10),
        Message: message.trim(),
      });

      toast.success('Thank you for your feedback!');
      handleClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error?.ExceptionMessage || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  }, [feedbackTypeId, message, submitFeedback, handleClose]);

  const isValid = feedbackTypeId && message.trim().length > 0;

  return (
    <>
      <Tooltip title="Create Feedback" arrow>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={dialog.onTrue}
          sx={{ width: 48, height: 48 }}
          {...other}
        >
          <Iconify icon="solar:add-circle-bold-duotone" width={40} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={dialog.value}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                color: 'primary.main',
              }}
            >
              <Iconify icon="solar:chat-round-dots-bold-duotone" width={28} />
            </Box>
            <Box>
              <Box sx={{ typography: 'h6' }}>Send Feedback</Box>
              <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                Share your thoughts with us
              </Box>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              select
              fullWidth
              label="Feedback Type"
              value={feedbackTypeId}
              onChange={(e) => setFeedbackTypeId(e.target.value)}
              disabled={feedbackTypesLoading || loading}
              helperText={
                feedbackTypesLoading
                  ? 'Loading feedback types...'
                  : 'Select a category for your feedback'
              }
            >
              {feedbackTypes?.map((feedbackType) => (
                <MenuItem key={feedbackType.Id} value={feedbackType.Id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon={feedbackType.Icon} color={feedbackType.ColorCode} width={20} />
                    <Box>{feedbackType.Name}</Box>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              placeholder="Please describe your feedback in detail..."
              helperText={`${message.length} / 2000 characters`}
              inputProps={{
                maxLength: 2000,
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            startIcon={
              loading ? (
                <Iconify icon="svg-spinners:180-ring-with-bg" width={20} />
              ) : (
                <Iconify icon="mingcute:send-fill" width={20} />
              )
            }
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
